import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getCachedDictionaryFallbackChain,
  getDictionaryFallbackChain,
  loadLocale,
  resolveLocaleCode,
  isBundledLocale,
} from '../locales';
import { translateWithFallback } from '../lib/translate';
import {
  clearAllUserData,
  clearAuthSessionFlags,
  canEnterMainApp,
  isGuestStorage,
  isLoggedInStorage,
  getNationality,
  loadLanguage,
  saveLanguage,
  saveNationality,
} from '../lib/onboardingStorage';
import { clearSupabaseLocalAuthStorage, getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { SESSION_RESTORE_MS, waitForSupabaseSession } from '../lib/authSession';
import { findNearestLocation, resolveCityLocationId } from '../api/locationApi';
import { resolveLocationCityCode } from '../lib/cityCode';
import { saveMySelectedRegions, syncMyLanguageToDb } from '../api/profileApi';
import { setSentryUser } from '../lib/sentry';
import { resetAdStatsUserCache } from '../utils/adMatcher';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { isFeatureEnabled as featureMapEnabled } from '../lib/featureFlags';
import { getCountryCodeForCity } from '../api/regionWaitlistApi';
import { slugifyRegion } from '../utils/regionSlugs';
import { getAds as getRankedAdsCached, pickCachedAd } from '../lib/getAds';
import { syncCloudAdsFromSupabase } from '../lib/cloudAdPool';
import {
  buildColdstartGpsSlot,
  COLDSTART_DEFAULT_CITY,
  clearRegionSlots,
  effectiveSlotLabel,
  fetchSlotLabel,
  fetchSlotSpecificLabel,
  getActiveRegion,
  isValidLocationValue,
  loadActive,
  loadGpsSlot,
  loadPickSlot,
  locationValueFromLocationRow,
  migrateLegacyRegionStorageOnce,
  normalizeLocationValue,
  saveActive,
  saveGpsSlot,
  savePickSlot,
} from '../lib/regionSlots';
import { buildActiveRegionView } from '../lib/activeRegionView';

function initialLanguage() {
  const stored = loadLanguage();
  if (stored) return resolveLocaleCode(stored);
  return 'ko';
}

function initialSearchRadius() {
  if (typeof window === 'undefined') return 10;
  return parseInt(window.localStorage.getItem('mingo_radius') ?? '10', 10) || 10;
}

function initialNationality() {
  return getNationality();
}

function readAuth() {
  return {
    isGuest: isGuestStorage(),
    isLoggedIn: isLoggedInStorage(),
  };
}

const MingoContext = createContext(null);

export function MingoProvider({ children }) {
  const initialRadius = initialSearchRadius();

  const [gpsSlot, setGpsSlotState] = useState(() => loadGpsSlot());
  const [pickSlot, setPickSlotState] = useState(() => loadPickSlot());
  const [activeSource, setActiveSourceState] = useState(() => loadActive());
  const [gpsSlotLabel, setGpsSlotLabel] = useState('');
  const [pickSlotLabel, setPickSlotLabel] = useState(() => {
    const slot = loadPickSlot();
    return slot?.city_label ? String(slot.city_label) : '';
  });
  /** 가장 구체적인 지명만(구/군 있으면 그것만) — 날씨 위젯 등에서 사용 */
  const [gpsSlotSpecificLabel, setGpsSlotSpecificLabel] = useState('');
  const [pickSlotSpecificLabel, setPickSlotSpecificLabel] = useState('');

  const [language, setLanguageState] = useState(initialLanguage);
  const [dictChain, setDictChain] = useState(() => getCachedDictionaryFallbackChain(initialLanguage()));
  const [nationality, setNationalityState] = useState(initialNationality);
  const [auth, setAuth] = useState(readAuth);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  /** 활성 위치 탭 (0=제1, 1=제2, null=기본·제1 강조) */
  const [activeRegionSlot, setActiveRegionSlot] = useState(null);
  const { featureFlagMap, refreshFeatureFlags, featureFlagsLoading } = useFeatureFlags();
  /** @type {React.MutableRefObject<null | (() => void | Promise<void>)>} */
  const afterLoginActionRef = useRef(null);
  const [gpsLocationRow, setGpsLocationRow] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [userCoords, setUserCoords] = useState(null);
  const [gpsUnmapped, setGpsUnmapped] = useState(false);
  const [searchRadius, setSearchRadius] = useState(initialRadius);
  const [userType, setUserType] = useState(window.localStorage.getItem('mingo_user_type') ?? 'expat');
  const wasHiddenForGpsRef = useRef(false);
  const initialGpsFetchDoneRef = useRef(false);
  const migrationDoneRef = useRef(false);
  const gpsUnmappedNotifyRef = useRef(() => {});

  /** @deprecated STEP C — 읽기 전용 파생; 소비처는 getActiveRegion으로 이전 */
  const selectedRegions = useMemo(() => {
    const out = [];
    if (gpsSlotLabel) out.push(gpsSlotLabel);
    const pickLabel = effectiveSlotLabel(pickSlot, pickSlotLabel);
    if (pickLabel && (!gpsSlotLabel || pickLabel !== gpsSlotLabel)) out.push(pickLabel);
    return out;
  }, [gpsSlotLabel, pickSlot, pickSlotLabel]);

  const setSearchRadiusPersist = useCallback((r) => {
    setSearchRadius(r);
    window.localStorage.setItem('mingo_radius', String(r));
  }, []);

  const setUserTypePersist = useCallback((type) => {
    setUserType(type);
    window.localStorage.setItem('mingo_user_type', type);
  }, []);

  const setGpsSlot = useCallback((updater) => {
    setGpsSlotState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveGpsSlot(next);
      return next;
    });
  }, []);

  const setPickSlot = useCallback((updater, opts = {}) => {
    setPickSlotState((prev) => {
      const raw = typeof updater === 'function' ? updater(prev) : updater;
      if (raw == null) {
        savePickSlot(null);
        return null;
      }
      const labelHint = String(opts?.label || raw.city_label || '').trim();
      const next = normalizeLocationValue({
        ...raw,
        ...(labelHint ? { city_label: labelHint } : {}),
      });
      savePickSlot(next);
      return next;
    });
    if (opts?.label) {
      setPickSlotLabel(String(opts.label));
    } else if (typeof updater !== 'function') {
      if (updater == null) {
        setPickSlotLabel('');
      } else {
        const hint = String(updater.city_label || '').trim();
        if (hint) setPickSlotLabel(hint);
      }
    }
  }, []);

  const setActiveSource = useCallback((source) => {
    const next = source === 'pick' || source === 'interest' ? 'pick' : 'gps';
    setActiveSourceState(next);
    saveActive(next);
    setActiveRegionSlot(next === 'pick' ? 1 : 0);
  }, []);

  const ensureGpsSlotDefault = useCallback(async () => {
    if (isValidLocationValue(gpsSlot)) return;
    const fallback = await buildColdstartGpsSlot({ radius_km: searchRadius });
    if (!fallback) return;
    setGpsSlotState(fallback);
    saveGpsSlot(fallback);
    setGpsSlotLabel(COLDSTART_DEFAULT_CITY.city_label);
  }, [gpsSlot, searchRadius]);

  useEffect(() => {
    if (migrationDoneRef.current) return undefined;
    migrationDoneRef.current = true;
    void migrateLegacyRegionStorageOnce().then((state) => {
      setGpsSlotState((prev) => (prev?.city_id ? prev : state.gpsSlot));
      setPickSlotState((prev) => (prev?.city_id ? prev : state.pickSlot));
      setActiveSourceState((prev) => (prev === 'pick' ? prev : state.activeSource));
    });
    return undefined;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchSlotLabel(gpsSlot, language, gpsLocationRow).then((label) => {
      if (!cancelled) setGpsSlotLabel(label);
    });
    void fetchSlotSpecificLabel(gpsSlot, language, gpsLocationRow).then((label) => {
      if (!cancelled) setGpsSlotSpecificLabel(label);
    });
    return () => {
      cancelled = true;
    };
  }, [gpsSlot, language, gpsLocationRow]);

  useEffect(() => {
    let cancelled = false;
    if (!pickSlot?.city_id) {
      setPickSlotLabel('');
      setPickSlotSpecificLabel('');
      return () => {
        cancelled = true;
      };
    }
    const stored = effectiveSlotLabel(pickSlot, '');
    if (stored) {
      setPickSlotLabel(stored);
      setPickSlotSpecificLabel(stored);
    }
    void fetchSlotLabel(pickSlot, language).then((label) => {
      if (!cancelled) setPickSlotLabel(effectiveSlotLabel(pickSlot, label));
    });
    void fetchSlotSpecificLabel(pickSlot, language).then((label) => {
      if (!cancelled) setPickSlotSpecificLabel(effectiveSlotLabel(pickSlot, label));
    });
    return () => {
      cancelled = true;
    };
  }, [pickSlot, language]);

  useEffect(() => {
    void ensureGpsSlotDefault();
  }, [ensureGpsSlotDefault]);

  const requestUserLocation = useCallback(() => {
    if (!navigator.geolocation) return Promise.resolve(null);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setUserCoords(coords);
          try {
            const loc = await findNearestLocation(coords.lat, coords.lng, 'city');
            if (loc) {
              setGpsUnmapped(false);
              setGpsLocationRow(loc);
              setCurrentLocation(loc);
              const slot = locationValueFromLocationRow(loc, {
                lat: coords.lat,
                lng: coords.lng,
                radius_km: searchRadius,
              });
              if (slot) {
                setGpsSlotState(slot);
                saveGpsSlot(slot);
              }
            } else {
              setGpsUnmapped(true);
              const existing = loadGpsSlot();
              if (!isValidLocationValue(existing)) {
                await ensureGpsSlotDefault();
              }
              gpsUnmappedNotifyRef.current();
            }
          } catch {
            const existing = loadGpsSlot();
            if (!isValidLocationValue(existing)) {
              await ensureGpsSlotDefault();
            }
          }
          resolve(coords);
        },
        (err) => {
          console.warn('위치 권한 거부:', err);
          resolve(null);
        },
        { enableHighAccuracy: false, maximumAge: 60_000, timeout: 12_000 },
      );
    });
  }, [ensureGpsSlotDefault, searchRadius]);

  useEffect(() => {
    if (!canEnterMainApp() || initialGpsFetchDoneRef.current) return undefined;
    initialGpsFetchDoneRef.current = true;
    void requestUserLocation();
    return undefined;
  }, [requestUserLocation]);

  useEffect(() => {
    function onVisForGps() {
      if (document.visibilityState === 'hidden') {
        wasHiddenForGpsRef.current = true;
        return;
      }
      if (
        document.visibilityState === 'visible' &&
        wasHiddenForGpsRef.current &&
        canEnterMainApp()
      ) {
        wasHiddenForGpsRef.current = false;
        void requestUserLocation();
      }
    }
    document.addEventListener('visibilitychange', onVisForGps);
    return () => document.removeEventListener('visibilitychange', onVisForGps);
  }, [requestUserLocation]);

  useEffect(() => {
    function syncSlotsFromStorage() {
      const gps = loadGpsSlot();
      const pick = loadPickSlot();
      setGpsSlotState(gps);
      setPickSlotState(pick);
      setActiveSourceState(loadActive());
      if (pick?.city_label) {
        setPickSlotLabel(String(pick.city_label));
      } else if (!pick?.city_id) {
        setPickSlotLabel('');
      }
    }
    function syncNationalityFromStorage() {
      const next = getNationality();
      setNationalityState((prev) => (prev === next ? prev : next));
    }
    function onVis() {
      if (document.visibilityState === 'visible') {
        syncSlotsFromStorage();
        syncNationalityFromStorage();
      }
    }
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', syncSlotsFromStorage);
    window.addEventListener('focus', syncNationalityFromStorage);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', syncSlotsFromStorage);
      window.removeEventListener('focus', syncNationalityFromStorage);
    };
  }, []);

  const clearGpsUnmapped = useCallback(() => {
    setGpsUnmapped(false);
  }, []);

  const refreshAuth = useCallback(() => {
    setAuth(readAuth());
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) return undefined;
    const sb = getSupabase();
    if (!sb) return undefined;
    const { data } = sb.auth.onAuthStateChange((event, session) => {
      refreshAuth();
      if (session?.user?.id) {
        setSentryUser({ id: session.user.id });
      } else {
        setSentryUser(null);
      }
      if (event === 'TOKEN_REFRESHED' && session) return;
      if (event === 'SIGNED_IN' && session) return;
      if (event === 'INITIAL_SESSION') {
        return;
      }
      if (!session && (event === 'SIGNED_OUT' || event === 'USER_DELETED')) {
        if (isLoggedInStorage() && !isGuestStorage()) {
          clearAuthSessionFlags();
          refreshAuth();
          const p = window.location.pathname || '';
          if (!p.startsWith('/onboarding') && !p.startsWith('/splash') && p !== '/') {
            const q = window.location.search || '';
            const nextTarget =
              p === '/edit-profile' || p === '/settings/account' || p === '/profile' ? '/home' : `${p}${q}`;
            window.location.assign(`/onboarding/auth?next=${encodeURIComponent(nextTarget)}`);
          }
        }
      }
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, [refreshAuth]);

  useEffect(() => {
    if (!isSupabaseConfigured) return undefined;
    const sb = getSupabase();
    if (!sb) return undefined;
    function syncSession() {
      const expectSession = isLoggedInStorage() && !isGuestStorage();
      void waitForSupabaseSession(sb, { maxMs: SESSION_RESTORE_MS, expectSession }).then((session) => {
        if (!session && expectSession) {
          clearAuthSessionFlags();
        }
        refreshAuth();
      });
    }
    function onFocus() {
      void sb.auth.refreshSession().catch(() => {
        /* offline */
      });
      syncSession();
    }
    const onVis = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    const id = window.setInterval(syncSession, 120000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshAuth]);

  const resetAfterLogout = useCallback(async () => {
    const sb = getSupabase();
    if (sb) {
      try {
        await sb.auth.signOut({ scope: 'global' });
      } catch {
        /* ignore */
      }
      clearSupabaseLocalAuthStorage();
    }
    resetAdStatsUserCache();
    clearAllUserData();
    clearRegionSlots();
    setGpsSlotState(null);
    setPickSlotState(null);
    setActiveSourceState('gps');
    setGpsSlotLabel('');
    setPickSlotLabel('');
    setGpsLocationRow(null);
    setCurrentLocation(null);
    setUserCoords(null);
    setGpsUnmapped(false);
    setActiveRegionSlot(null);
    setLanguageState('ko');
    setNationalityState(initialNationality());
    setAuth(readAuth());
    setSentryUser(null);
  }, []);

  /** GPS·관심지역 슬롯 초기화 후 다낭 콜드스타트 기본값 적용 */
  const resetLocationSlots = useCallback(async () => {
    clearRegionSlots();
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem('mingo_gps_cache');
        window.localStorage.removeItem('mingo_interest_pick');
        window.localStorage.removeItem('mingo_regions');
        window.localStorage.removeItem('selectedRegions');
        window.localStorage.removeItem('mingo_active_source');
      } catch {
        /* ignore */
      }
    }
    setGpsSlotState(null);
    setPickSlotState(null);
    setActiveSourceState('gps');
    saveActive('gps');
    setGpsSlotLabel('');
    setPickSlotLabel('');
    setGpsLocationRow(null);
    setCurrentLocation(null);
    setUserCoords(null);
    setGpsUnmapped(false);
    setActiveRegionSlot(0);
    const fallback = await buildColdstartGpsSlot({ radius_km: searchRadius });
    if (fallback) {
      saveGpsSlot(fallback);
      setGpsSlotState(fallback);
      setGpsSlotLabel(COLDSTART_DEFAULT_CITY.city_label);
    }
    window.location.reload();
  }, [searchRadius]);

  /** @deprecated STEP C — pick 슬롯만 갱신 (onboarding 호환) */
  const setSelectedRegions = useCallback(
    (updater) => {
      const prev = selectedRegions;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const arr = Array.isArray(next) ? next.slice(0, 2) : [];
      const pickLabel = String(arr[1] || '').trim() || (arr.length === 1 ? String(arr[0] || '').trim() : '');
      if (!pickLabel) {
        setPickSlot(null);
        return;
      }
      void (async () => {
        const cc = getCountryCodeForCity(pickLabel) || 'VN';
        const cityId = await resolveCityLocationId(pickLabel, { countryCode: cc });
        if (cityId == null) return;
        setPickSlot(
          normalizeLocationValue({
            country_code: cc,
            city_id: String(cityId),
            radius_km: searchRadius,
            city_label: pickLabel,
            city_code: resolveLocationCityCode(pickLabel),
          }),
          { label: pickLabel },
        );
      })();
    },
    [searchRadius, selectedRegions, setPickSlot],
  );

  /** @deprecated — setGpsSlot 사용 */
  const setGps = useCallback(
    (updater) => {
      setGpsSlot(updater);
    },
    [setGpsSlot],
  );

  /** @deprecated — setPickSlot 사용 */
  const setPicksInterest = useCallback(
    (interest) => {
      if (!interest) {
        setPickSlot(null);
        return;
      }
      const slot = normalizeLocationValue({
        country_code: interest.country_code,
        city_id: interest.city_id,
        district_id: interest.district_id,
        lat: interest.lat,
        lng: interest.lng,
        radius_km: interest.radius_km,
      });
      if (slot) {
        setPickSlot(slot, { label: interest.city_label || slot.city_label });
        return;
      }
      if (interest.city_label) {
        void (async () => {
          const cc = interest.country_code || getCountryCodeForCity(interest.city_label) || 'VN';
          const cityId = await resolveCityLocationId(interest.city_label, { countryCode: cc });
          if (cityId == null) return;
          setPickSlot(
            normalizeLocationValue({
              country_code: cc,
              city_id: String(cityId),
              district_id: interest.district_id,
              lat: interest.lat,
              lng: interest.lng,
              radius_km: interest.radius_km,
              city_label: interest.city_label,
            }),
            { label: interest.city_label },
          );
        })();
      }
    },
    [setPickSlot],
  );

  const resolveActiveLocation = useCallback(() => {
    const region = getActiveRegion(gpsSlot, pickSlot, activeSource);
    if (!region) {
      return {
        country_code: 'VN',
        city_label: '',
        label: '',
        source: 'gps',
      };
    }
    const usePick = activeSource === 'pick' && pickSlot?.city_id === region.city_id;
    const label = usePick
      ? effectiveSlotLabel(pickSlot, pickSlotLabel)
      : gpsSlotLabel;
    return {
      ...region,
      city_label: label || region.city_label || '',
      label,
      source: usePick ? 'interest' : 'gps',
    };
  }, [gpsSlot, pickSlot, activeSource, gpsSlotLabel, pickSlotLabel]);

  const activeRegionView = useMemo(
    () =>
      buildActiveRegionView({
        gpsSlot,
        pickSlot,
        activeSource,
        gpsSlotLabel,
        pickSlotLabel,
        gpsSlotSpecificLabel,
        pickSlotSpecificLabel,
        locationCode: activeSource === 'pick' ? pickSlot?.city_code : gpsLocationRow?.code,
      }),
    [
      gpsSlot,
      pickSlot,
      activeSource,
      gpsSlotLabel,
      pickSlotLabel,
      gpsSlotSpecificLabel,
      pickSlotSpecificLabel,
      gpsLocationRow?.code,
    ],
  );

  const syncSelectedRegions = useCallback(
    async (regions) => {
      if (!isSupabaseConfigured || !auth.isLoggedIn) return { ok: true, message: '' };
      const res = await saveMySelectedRegions(regions);
      if (res?.success) return { ok: true, message: '' };
      const msg = res?.message || 'UPDATE_FAILED';
      return { ok: false, message: String(msg) };
    },
    [auth.isLoggedIn],
  );

  const setLanguage = useCallback((code) => {
    if (code == null) return;
    const c = String(code).trim();
    const resolved = resolveLocaleCode(c);
    saveLanguage(isBundledLocale(c) ? c : resolved);
    setLanguageState(resolved);
    void loadLocale(resolved);
    void syncMyLanguageToDb(resolved);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setDictChain(getCachedDictionaryFallbackChain(language));
    void getDictionaryFallbackChain(language)
      .then((chain) => {
        if (cancelled) return;
        setDictChain(chain.length ? chain : getCachedDictionaryFallbackChain(language));
      })
      .catch(() => {
        if (cancelled) return;
        setDictChain(getCachedDictionaryFallbackChain(language));
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const setNationality = useCallback((code) => {
    if (code == null) return;
    const c = String(code).trim();
    saveNationality(c);
    setNationalityState(c);
  }, []);

  const showToast = useCallback((message, options = {}) => {
    if (!message) return;
    const parsed = typeof options === 'number' ? { ms: options } : options || {};
    const ms = Number(parsed.ms) > 0 ? Number(parsed.ms) : 2600;
    const type = ['success', 'error', 'warning', 'info'].includes(parsed.type) ? parsed.type : 'info';
    setToast({ message: String(message), type });
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => setToast(null), ms);
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToast(null);
  }, []);

  const openLoginRequired = useCallback((onAfterSuccess) => {
    afterLoginActionRef.current = typeof onAfterSuccess === 'function' ? onAfterSuccess : null;
    setLoginModalOpen(true);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const closeLoginRequired = useCallback(() => {
    afterLoginActionRef.current = null;
    setLoginModalOpen(false);
  }, []);

  const completeLoginInModal = useCallback(() => {
    setLoginModalOpen(false);
    const fn = afterLoginActionRef.current;
    afterLoginActionRef.current = null;
    if (fn) {
      window.setTimeout(() => {
        void Promise.resolve()
          .then(() => fn())
          .catch(() => {});
      }, 0);
    }
  }, []);

  const t = useCallback(
    (key, params) => translateWithFallback(dictChain, key, params),
    [dictChain],
  );

  gpsUnmappedNotifyRef.current = () => {
    showToast(t('location.gpsUnmapped'), { type: 'info', ms: 3200 });
  };

  const activeRegion = getActiveRegion(gpsSlot, pickSlot, activeSource);
  const primaryRegionLabel = gpsSlotLabel || 'Da Nang';
  const activeRegionLabel =
    activeRegionSlot === 1
      ? effectiveSlotLabel(pickSlot, pickSlotLabel) || null
      : activeRegionSlot === 0
        ? gpsSlotLabel || null
        : null;
  const city = activeRegionLabel || primaryRegionLabel;
  const currentCountry = useMemo(
    () => activeRegion?.country_code || getCountryCodeForCity(primaryRegionLabel) || 'VN',
    [activeRegion, primaryRegionLabel],
  );
  const currentRegion = useMemo(() => slugifyRegion(primaryRegionLabel) || 'danang', [primaryRegionLabel]);
  const displayCurrencyCode = useMemo(() => {
    const cc = currentCountry;
    if (cc === 'VN') return 'VND';
    if (cc === 'TH') return 'THB';
    if (cc === 'KR') return 'KRW';
    if (cc === 'JP') return 'JPY';
    if (cc === 'CN') return 'CNY';
    if (cc === 'TW') return 'TWD';
    if (cc === 'ID') return 'IDR';
    if (cc === 'PH') return 'PHP';
    if (cc === 'MY') return 'MYR';
    if (cc === 'SG') return 'SGD';
    if (cc === 'AU') return 'AUD';
    if (cc === 'GB') return 'GBP';
    if (cc === 'FR' || cc === 'DE' || cc === 'NL') return 'EUR';
    if (cc === 'CA') return 'CAD';
    return 'USD';
  }, [currentCountry]);

  const gps = useMemo(
    () => ({
      coords:
        gpsSlot?.lat != null && gpsSlot?.lng != null
          ? { lat: gpsSlot.lat, lng: gpsSlot.lng }
          : userCoords,
      location: gpsLocationRow,
      label: gpsSlotLabel,
    }),
    [gpsSlot, gpsLocationRow, gpsSlotLabel, userCoords],
  );

  const picks = useMemo(
    () => ({
      interest: pickSlot
        ? {
            ...pickSlot,
            city_label: effectiveSlotLabel(pickSlot, pickSlotLabel),
          }
        : null,
    }),
    [pickSlot, pickSlotLabel],
  );

  const active = useMemo(
    () => ({
      source: activeSource === 'pick' ? 'interest' : 'gps',
      radiusKm: searchRadius,
    }),
    [activeSource, searchRadius],
  );

  const isFeatureEnabled = useCallback((key) => featureMapEnabled(featureFlagMap, key), [featureFlagMap]);

  const getAds = useCallback(
    (partial = {}) =>
      getRankedAdsCached({
        language,
        regions: selectedRegions,
        countryCode: currentCountry,
        ...partial,
      }),
    [language, selectedRegions, currentCountry],
  );

  const pickAdForContext = useCallback(
    (partial = {}, slotIndex = 0) =>
      pickCachedAd(
        {
          language,
          regions: selectedRegions,
          countryCode: currentCountry,
          ...partial,
        },
        slotIndex,
      ),
    [language, selectedRegions, currentCountry],
  );

  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      try {
        await syncCloudAdsFromSupabase();
      } catch {
        // no-op
      }
    };
    void sync();
    const timer = window.setInterval(() => {
      if (!cancelled) void sync();
    }, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [language, selectedRegions, currentCountry]);

  const value = useMemo(
    () => ({
      selectedRegions,
      setSelectedRegions,
      syncSelectedRegions,
      gpsSlot,
      pickSlot,
      activeSource,
      gpsSlotLabel,
      pickSlotLabel,
      setGpsSlot,
      setPickSlot,
      gps,
      picks,
      active,
      setGps,
      setPicksInterest,
      setActiveSource,
      resolveActiveLocation,
      activeRegionView,
      activeRegionSlot,
      setActiveRegionSlot,
      activeRegionLabel,
      language,
      currentLanguage: language,
      setLanguage,
      nationality,
      setNationality,
      city,
      t,
      isGuest: auth.isGuest,
      isLoggedIn: auth.isLoggedIn,
      refreshAuth,
      showToast,
      dismissToast,
      openLoginRequired,
      closeLoginRequired,
      completeLoginInModal,
      loginModalOpen,
      toastMessage: toast,
      resetAfterLogout,
      resetLocationSlots,
      featureFlagMap,
      featureFlagsLoading,
      refreshFeatureFlags,
      isFeatureEnabled,
      currentRegion,
      currentCountry,
      primaryRegionLabel,
      displayCurrencyCode,
      getAds,
      pickAdForContext,
      currentLocation,
      setCurrentLocation,
      userCoords,
      gpsUnmapped,
      clearGpsUnmapped,
      requestUserLocation,
      searchRadius,
      setSearchRadius: setSearchRadiusPersist,
      userType,
      setUserType: setUserTypePersist,
    }),
    [
      selectedRegions,
      setSelectedRegions,
      syncSelectedRegions,
      gpsSlot,
      pickSlot,
      activeSource,
      gpsSlotLabel,
      pickSlotLabel,
      setGpsSlot,
      setPickSlot,
      gps,
      picks,
      active,
      setGps,
      setPicksInterest,
      setActiveSource,
      resolveActiveLocation,
      activeRegionView,
      activeRegionSlot,
      setActiveRegionSlot,
      activeRegionLabel,
      language,
      setLanguage,
      nationality,
      setNationality,
      city,
      t,
      auth.isGuest,
      auth.isLoggedIn,
      refreshAuth,
      resetAfterLogout,
      resetLocationSlots,
      showToast,
      dismissToast,
      openLoginRequired,
      closeLoginRequired,
      completeLoginInModal,
      loginModalOpen,
      toast,
      featureFlagMap,
      featureFlagsLoading,
      refreshFeatureFlags,
      isFeatureEnabled,
      currentRegion,
      currentCountry,
      primaryRegionLabel,
      displayCurrencyCode,
      getAds,
      pickAdForContext,
      currentLocation,
      userCoords,
      gpsUnmapped,
      clearGpsUnmapped,
      requestUserLocation,
      searchRadius,
      userType,
      setSearchRadiusPersist,
      setUserTypePersist,
    ],
  );

  return (
    <MingoContext.Provider value={value}>
      {children}
    </MingoContext.Provider>
  );
}

export function useMingo() {
  const ctx = useContext(MingoContext);
  if (!ctx) {
    throw new Error('useMingo must be used within MingoProvider');
  }
  return ctx;
}

/** Alias for screens that prefer `useLanguage` naming. */
export function useLanguage() {
  return useMingo();
}
